import type { FC, ReactNode } from 'react'
import { Box } from '@mui/material'
import { iconColumnSx } from './MostRecentEventPreview'
import {
  CONVERSATION_REVEAL_CLASS,
  conversationColumnSx,
  detailActivityRowSx,
  detailColumnSx,
  orchestratorContainerSx,
  transitioningContainerSx,
} from './sessionCardStyles'

interface SessionCardLayoutProps {
  // Conversation-button content. When provided, the layout wraps it in the
  // hover-revealed left column. Omit to render the card without the column.
  readonly conversation?: ReactNode
  // Right-column row 1: the event preview OR the transient prompt block.
  readonly preview?: ReactNode
  // Right-column row 2 leading slot: the reply button. The layout wraps it in
  // the shared icon column so it aligns with preview/activity leading icons.
  readonly reply?: ReactNode
  // Right-column row 2: the activity element. The caller supplies the live
  // (hook-coupled) or static (prop-driven) variant.
  readonly activity: ReactNode
  readonly isTransitioning?: boolean
}

/**
 * Pure presentational shell for a session card. Owns the column-major layout
 * (hover-revealed conversation column + detail column with a preview row over an
 * activity row) shared by the live Events page and the marketing demo. Holds no
 * hooks or contexts; behavior is supplied entirely through slots.
 */
export const SessionCardLayout: FC<SessionCardLayoutProps> = ({
  conversation,
  preview,
  reply,
  activity,
  isTransitioning = false,
}) => {
  return (
    <Box sx={isTransitioning ? transitioningContainerSx : orchestratorContainerSx}>
      {conversation !== undefined && (
        <Box className={CONVERSATION_REVEAL_CLASS} sx={conversationColumnSx}>
          {conversation}
        </Box>
      )}

      <Box sx={detailColumnSx}>
        {preview}

        <Box sx={detailActivityRowSx}>
          {reply !== undefined && <Box sx={iconColumnSx}>{reply}</Box>}
          {activity}
        </Box>
      </Box>
    </Box>
  )
}
